/**
 * CALL FOR A TEST - the GM asks somebody to roll, and the app rolls it.
 *
 * A call names who rolls, what they test (a skill or a characteristic) and
 * how hard it is; optionally who opposes them, testing something of their
 * own (Scrutiny against Deceive); optionally the whole group at once; and
 * whether the result is the GM's alone.
 *
 * This file is the pure half: the call as data (it crosses the wire inside
 * an ask, so it is validated on the way in), what each combatant stands on
 * when asked, and the resolution - dice through the existing single-test
 * and opposed-test rollers, one log entry per person asked, in the order
 * they were named. An opposed call rolls the ASKED side first, then the
 * opposition, for each person in turn (the opposed roller's own contract).
 *
 * A STATBLOCK HAS NO SKILLS. Asked for one, it tests the skill's governing
 * characteristic at full value: a sentry asked for Awareness rolls
 * Perception. A character asked for an advanced skill they do not have
 * cannot roll at all, and the entry says so in a sentence.
 *
 * Zero React dependency. Dice via engine/dice.
 */
import { effectiveSheet } from '../roster/stancedSheet';
import {
  CHARACTERISTIC_TEST_LABELS,
  SKILL_TEST_DEFS,
  buildTestNarrative,
  calcSkillTarget,
  rollCharacteristicTest,
  rollSkillTest,
} from '../characteristicTests';
import { buildOpposedNarrative, rollOpposedTest } from '../opposedTests';
import { assistMod, clampAssists } from './assist';
import type { OpposedSide, TestSubject } from '../opposedTests';
import type { StructuredNarrativeEntry } from '../structuredNarrative';
import type { UnnaturalDosMode } from '../unnaturalDos';
import type { CharacteristicId, SkillId } from '../character/types';
import type { Combatant } from '../roster/types';

export type { TestSubject };

/** The side that opposes: one combatant, their own subject and difficulty. */
interface Opposition {
  who: string;
  subject: TestSubject;
  difficulty: number;
}

export interface TestCall {
  /** The combatants asked, in the order they roll. Several is a group test. */
  who: string[];
  subject: TestSubject;
  /** The difficulty modifier on the asked side: +0 Challenging, -20 Hard... */
  difficulty: number;
  against: Opposition | null;
  /** The result is the GM's alone: it is not logged, and the player sees only that they were asked. */
  secret: boolean;
  /** How many are helping the asked side; each adds the table's assist bonus (`assist.ts`). Absent = none. */
  assists?: number;
  /**
   * An EXTENDED test: every degree by which someone passes fills a segment
   * of this clock (by id). Absent = an ordinary test. The roll knows nothing
   * of the clock; whoever holds the clocks fills it.
   */
  fills?: string | null;
  /** A board entry (by id) shown to the table the moment anyone asked passes. Absent = nothing is revealed. */
  reveals?: string | null;
}

const skillDef = (id: SkillId) => SKILL_TEST_DEFS.find((d) => d.id === id);

/** "Scrutiny", "Perception" - what a subject is called at the table. */
export function subjectLabel(subject: TestSubject): string {
  if (subject.kind === 'skill') return skillDef(subject.id)?.label ?? subject.id;
  return CHARACTERISTIC_TEST_LABELS[subject.id] ?? subject.id;
}

type Footing = { side: OpposedSide } | { refusal: string };

/** What this combatant rolls for this subject, or the sentence that says why they cannot. */
export function footingFor(c: Combatant, subject: TestSubject): Footing {
  if (subject.kind === 'characteristic') {
    return { side: { kind: 'characteristic', character: effectiveSheet(c), charId: subject.id } };
  }
  const def = skillDef(subject.id);
  if (!def) return { refusal: `${c.name} cannot test ${subject.id}: no such skill.` };
  // A statblock has no skills: the governing characteristic, at full value.
  if (c.kind === 'statblock')
    return { side: { kind: 'characteristic', character: effectiveSheet(c), charId: def.governing } };
  if (!calcSkillTarget(effectiveSheet(c), subject.id).rollable) {
    return { refusal: `${c.name} cannot test ${def.label}: an advanced skill they are not trained in.` };
  }
  return { side: { kind: 'skill', character: effectiveSheet(c), skillId: subject.id } };
}

export interface CalledResult {
  combatantId: string;
  name: string;
  entry: StructuredNarrativeEntry;
  /** Did the asked side come out ahead? Null when nothing was rolled. */
  passed: boolean | null;
  /**
   * How clearly, in degrees, always 0 or more: an opposed test's margin, or
   * a lone test's own degrees of success or failure. 0 when nothing was rolled.
   */
  margin: number;
}

interface ResolveOptions {
  unnaturalDosMode?: UnnaturalDosMode;
  /** What each helper adds (the table's ruling). Absent = +10, as written. */
  assistBonus?: number;
}

const refused = (c: Combatant, text: string): CalledResult => ({
  combatantId: c.id,
  name: c.name,
  entry: { summary: text, detail: {} },
  passed: null,
  margin: 0,
});

/** One person's unopposed test. */
function rollAlone(c: Combatant, side: OpposedSide, difficulty: number, opts: ResolveOptions): CalledResult {
  const unnatural = { mode: opts.unnaturalDosMode };
  const result =
    side.kind === 'skill'
      ? rollSkillTest(effectiveSheet(c), side.skillId, difficulty, 0, unnatural)
      : rollCharacteristicTest(
          effectiveSheet(c),
          (side as { charId: CharacteristicId }).charId,
          difficulty,
          0,
          unnatural,
        );
  return {
    combatantId: c.id,
    name: c.name,
    entry: buildTestNarrative(result, c.name),
    passed: result.success,
    margin: result.success ? result.dos : result.dof,
  };
}

interface Versus {
  opponent: Combatant;
  side: OpposedSide;
  difficulty: number;
  label: string;
}

/** One person's test against the opposition: the asked side rolls first. */
function rollAgainst(c: Combatant, side: OpposedSide, difficulty: number, versus: Versus, opts: ResolveOptions) {
  const result = rollOpposedTest(
    { ...side, mod: difficulty },
    { ...versus.side, mod: versus.difficulty },
    { unnaturalDosMode: opts.unnaturalDosMode },
  );
  const entry = buildOpposedNarrative(result, c.name, versus.opponent.name, versus.label);
  // A stand-off (nobody ahead) has no winner to be clear about; a lost test is as clear as the other side's win.
  return { combatantId: c.id, name: c.name, entry, passed: result.winner === 'actor', margin: result.margin };
}

/** The opposition's footing, or the sentence that stops the whole call. */
function versusFor(call: TestCall, roster: readonly Combatant[]): Versus | { refusal: string } | null {
  if (!call.against) return null;
  const opponent = roster.find((c) => c.id === call.against!.who);
  if (!opponent) return { refusal: 'The one they were to be tested against is no longer here.' };
  const footing = footingFor(opponent, call.against.subject);
  if ('refusal' in footing) return footing;
  const label = `${subjectLabel(call.subject)} against ${subjectLabel(call.against.subject)}`;
  return { opponent, side: footing.side, difficulty: call.against.difficulty, label };
}

/**
 * Roll the call. One result per person asked who is still on the roster,
 * in the order named. Someone who cannot roll gets a sentence, not a roll,
 * and consumes no dice.
 */
export function resolveTestCall(
  call: TestCall,
  roster: readonly Combatant[],
  opts: ResolveOptions = {},
): CalledResult[] {
  const versus = versusFor(call, roster);
  const asked = call.who.map((id) => roster.find((c) => c.id === id)).filter((c): c is Combatant => !!c);
  if (versus && 'refusal' in versus) return asked.map((c) => refused(c, versus.refusal));
  // Helpers make the asked side's test easier; the other side's is untouched.
  const difficulty = call.difficulty + assistMod(call.assists, opts.assistBonus ?? 10);
  return asked.map((c) => {
    const footing = footingFor(c, call.subject);
    if ('refusal' in footing) return refused(c, footing.refusal);
    if (versus) return rollAgainst(c, footing.side, difficulty, versus, opts);
    return rollAlone(c, footing.side, difficulty, opts);
  });
}

/** "2 of 3 passed" - a group test's own line; null for a call to one person. */
export function groupLine(call: TestCall, results: readonly CalledResult[]): string | null {
  const rolled = results.filter((r) => r.passed !== null);
  if (call.who.length < 2 || rolled.length === 0) return null;
  const passed = rolled.filter((r) => r.passed).length;
  return `${subjectLabel(call.subject)}, the group: ${passed} of ${rolled.length} passed.`;
}

const CHARACTERISTIC_IDS = Object.keys(CHARACTERISTIC_TEST_LABELS);

function readSubject(raw: unknown): TestSubject | null {
  const { kind, id } = (raw ?? {}) as { kind?: unknown; id?: unknown };
  if (kind === 'skill' && typeof id === 'string' && skillDef(id as SkillId)) return { kind, id: id as SkillId };
  if (kind === 'characteristic' && typeof id === 'string' && CHARACTERISTIC_IDS.includes(id)) {
    return { kind, id: id as CharacteristicId };
  }
  return null;
}

const number = (value: unknown): number => (typeof value === 'number' && Number.isFinite(value) ? value : 0);

function readOpposition(raw: unknown): Opposition | null {
  const stored = (raw ?? null) as { who?: unknown; subject?: unknown; difficulty?: unknown } | null;
  const subject = readSubject(stored?.subject);
  if (!stored || typeof stored.who !== 'string' || !stored.who || !subject) return null;
  return { who: stored.who, subject, difficulty: number(stored.difficulty) };
}

/** A call that crossed the wire, validated. Null when it names nobody or nothing this build can roll. */
export function readTestCall(raw: unknown): TestCall | null {
  const stored = (raw ?? {}) as Record<string, unknown>;
  const subject = readSubject(stored.subject);
  const who = Array.isArray(stored.who)
    ? stored.who.filter((id): id is string => typeof id === 'string' && id !== '')
    : [];
  if (!subject || who.length === 0) return null;
  return {
    who: [...new Set(who)],
    subject,
    difficulty: number(stored.difficulty),
    against: readOpposition(stored.against),
    secret: stored.secret === true,
    ...extras(stored),
  };
}

const idOf = (value: unknown): string | null => (typeof value === 'string' && value ? value : null);

/** The optional riders of a call, each present only when it says something. */
function extras(stored: Record<string, unknown>): Pick<TestCall, 'assists' | 'fills' | 'reveals'> {
  const assists = clampAssists(stored.assists);
  const fills = idOf(stored.fills);
  const reveals = idOf(stored.reveals);
  return { ...(assists ? { assists } : {}), ...(fills ? { fills } : {}), ...(reveals ? { reveals } : {}) };
}

/**
 * What a rolled result does beyond the log, for whoever holds the clocks
 * and the board: the segments an extended test fills (its degrees, when it
 * passed), and the entry a pass reveals. Nothing for a failure or a refusal.
 */
export function ridersOf(
  call: TestCall,
  result: CalledResult,
): { fill: { clock: string; by: number } | null; reveal: string | null } {
  if (result.passed !== true) return { fill: null, reveal: null };
  // A pass is worth at least one segment: a tie-break win has no margin, and still moved the work on.
  const fill = call.fills ? { clock: call.fills, by: Math.max(1, result.margin) } : null;
  return { fill, reveal: call.reveals ?? null };
}
